import React, { useEffect, useState } from "react";
import { fetchJson } from "../../lib/api";
import type { JournalEntryAudit } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

export const JournalEntryAuditModal = ({
  houseId,
  journalEntryId,
  onClose,
}: {
  houseId: number;
  journalEntryId: number;
  onClose: () => void;
}) => {
  const { t } = useI18n();
  const [audits, setAudits] = useState<JournalEntryAudit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    fetchJson<{ audits: JournalEntryAudit[] }>(
      `/api/houses/${houseId}/journal-entries/${journalEntryId}/audits`,
    )
      .then((response) => setAudits(response.audits))
      .catch((loadError) => {
        setError(
          loadError instanceof Error
            ? loadError.message
            : t("entries.auditLoadFailed", "Failed to load audits"),
        );
      })
      .finally(() => setIsLoading(false));
  }, [journalEntryId, houseId]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-3xl">
        <CardHeader className="relative">
          <CardTitle>{t("entries.auditTitle", "Journal entry audit log")}</CardTitle>
          <button
            type="button"
            className="absolute right-4 top-4 text-xl text-muted transition hover:text-ink"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            ×
          </button>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <p className="text-sm text-muted">{t("entries.auditLoading", "Loading audits...")}</p>
          ) : null}
          {error ? <p className="text-sm text-error">{error}</p> : null}
          {!audits.length ? (
            <p className="text-sm text-muted">
              {t("entries.auditEmpty", "No audit history yet.")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.date", "Date")}</TableHead>
                  <TableHead>{t("entries.change", "Change")}</TableHead>
                  <TableHead>{t("entries.user", "User")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audits.map((audit) => (
                  <TableRow key={audit.id}>
                    <TableCell>{new Date(audit.changedAt).toLocaleString()}</TableCell>
                    <TableCell>{audit.changeType}</TableCell>
                    <TableCell>{audit.changedBy ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
